import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@design-system';
import {
  TrialCircularProgress,
  TrialStep,
  TrialStepItem,
  TRIAL_LOOM_VIDEOS,
} from '@/features/trial-mode';

import { Eye, FileText, Mail } from 'lucide-react';

interface TrialProgressProps {
  onStartInvite?: () => void;
  onReviewResults?: () => void;
  hasInvitedClient?: boolean;
  hasCompletedIntakeForm?: boolean;
  hasCompletedReview?: boolean;
}

export function TrialProgress({
  onStartInvite,
  onReviewResults,
  hasInvitedClient = false,
  hasCompletedIntakeForm = false,
  hasCompletedReview = false,
}: TrialProgressProps) {
  const completedCount = [hasInvitedClient, hasCompletedIntakeForm, hasCompletedReview].filter(
    Boolean,
  ).length;
  const totalSteps = 3;
  const progressValue = (completedCount / totalSteps) * 100;
  const isAllComplete = completedCount === totalSteps;

  const steps: TrialStep[] = [
    {
      stepNumber: 1,
      title: 'Invite your first client',
      description: 'Send an invite to get started with your first LTC plan',
      hint: 'Try inviting yourself first to see the client experience',
      icon: <Mail className="size-4" />,
      isComplete: hasInvitedClient,
      isCurrent: !hasInvitedClient,
      onAction: onStartInvite,
      isClientCreationAction: true,
      videoUrl: TRIAL_LOOM_VIDEOS.inviteClient,
    },
    {
      stepNumber: 2,
      title: 'Complete the intake form',
      description: 'Gather the information needed to generate personalized results',
      icon: <FileText className="size-4" />,
      isComplete: hasCompletedIntakeForm,
      isCurrent: hasInvitedClient && !hasCompletedIntakeForm,
      videoUrl: TRIAL_LOOM_VIDEOS.intakeForm,
    },
    {
      stepNumber: 3,
      title: 'Review the results',
      description: 'Explore the personalized LTC plan and insights',
      icon: <Eye className="size-4" />,
      isComplete: hasCompletedReview,
      isCurrent: hasCompletedIntakeForm && !hasCompletedReview,
      onAction: onReviewResults,
      actionLabel: 'Review Results',
      videoUrl: TRIAL_LOOM_VIDEOS.reviewResults,
    },
  ];

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={isAllComplete ? '' : 'trial-progress'}
    >
      <AccordionItem
        value="trial-progress"
        className="transition-colors data-[state=open]:bg-transparent"
      >
        <AccordionTrigger className="flex items-center">
          <div className="flex flex-1 items-center gap-4">
            <div className="relative flex items-center justify-center">
              <TrialCircularProgress
                value={progressValue}
                size={36}
                strokeWidth={3}
              />
              <span className="absolute text-xs font-semibold text-gray-950">
                {completedCount}/{totalSteps}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-950">Quick start guide</h3>
          </div>
        </AccordionTrigger>
        <AccordionContent className="border-border divide-border divide-y border-t p-0">
          {steps.map(step => (
            <TrialStepItem
              key={step.stepNumber}
              {...step}
            />
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
